import type {ReactNode} from 'react'
import React, {useRef} from 'react'
import styled, {css} from 'styled-components'

import Carousel, {Bullet} from '@/components/carousel'
import type {RefType as CarouselRefType} from '@/components/carousel'
import type {Props as FeatureBlockProps} from '@/components/feature-block'
import FeatureBlock from '@/components/feature-block'
import {Text} from '@/components/text'
import {useGlobalStrings} from '@/providers/global-strings'
import {Section, TitleLayout} from '@/styles/grid'
import {tokens} from '@/theme'
import mq from '@/utils/media-query'

export interface Item extends Pick<FeatureBlockProps, 'image' | 'mirrored'> {
    quote: FeatureBlockProps['title']
    attribution: {
        name?: string | null
        title?: string | null
    }
}

interface Props {
    title?: string
    items: Item[]
    dataTestId?: string
}

const QuoteCarousel = React.forwardRef<HTMLElement, Props>(({title, items, dataTestId}, ref) => {
    const carouselRef = useRef<CarouselRefType>(null)
    const globalStrings = useGlobalStrings()

    return (
        <SectionWrapper ref={ref} data-testid={dataTestId}>
            {title && (
                <TitleLayout>
                    <Text $variant="heading-large" as="h2">
                        {title}
                    </Text>
                </TitleLayout>
            )}

            {items.length === 1 ? (
                <Quote {...items[0]} />
            ) : (
                <StyledCarousel ref={carouselRef}>
                    {items.map((item, i) => (
                        <Quote key={`${item.quote}-${i}`} {...item}>
                            <CarouselDots $showOn="desktop">
                                {items.map(({quote}, index) => (
                                    <Bullet
                                        key={`${quote}-${index}`}
                                        $isActive={index === i}
                                        onClick={() => carouselRef.current?.slideGoTo(index)}
                                        aria-label={`${globalStrings.panel} ${index + 1}`}
                                    />
                                ))}
                            </CarouselDots>
                        </Quote>
                    ))}
                </StyledCarousel>
            )}
        </SectionWrapper>
    )
})

interface QuoteProps extends Item {
    children?: ReactNode
}

const Quote = ({quote, attribution: {name, title}, children, ...props}: QuoteProps) => {
    return (
        <FeatureBlock withQuotes title={quote} titleVariant="title-base" {...props}>
            {(name || title) && (
                <Text>
                    {name && <strong>{name}</strong>} {title}
                </Text>
            )}
            {children}
        </FeatureBlock>
    )
}

const SectionWrapper = styled(Section)`
    display: grid;
    row-gap: ${tokens.spacing40};
`

const StyledCarousel = styled(Carousel)`
    grid-column-gap: ${tokens.spacing20};
`

interface CarouselDotsProps {
    $showOn?: 'mobile' | 'desktop'
}

const CarouselDots = styled.nav<CarouselDotsProps>(({$showOn}) => {
    const bp = 'sm'

    return css`
        display: grid;
        grid-auto-flow: column;
        gap: ${tokens.spacing4};
        align-items: center;
        justify-content: start;
        ${mq.max(bp)} {
            display: ${$showOn === 'desktop' && 'none'};
        }
        ${mq.min(bp)} {
            display: ${$showOn === 'mobile' && 'none'};
        }
    `
})

export default QuoteCarousel
